import React, { useState, useEffect } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { gql } from '@apollo/client';
import { toast } from 'react-hot-toast';
import DashboardLayout from '../../components/dashboard/DashboardLayout.jsx';
import CRUDTable from '../../components/crud/CRUDTable';
import CRUDModal from '../../components/crud/CRUDModal';
import DeleteConfirmModal from '../../components/crud/DeleteConfirmModal';

// GraphQL Queries y Mutations
const GET_ROLES = gql`
  query GetRoles {
    roles {
      id
      role_name
    }
  }
`;

const CREATE_ROLE = gql`
  mutation CreateRole($role_name: String!) {
    createRole(role_name: $role_name) {
      id
      role_name
    }
  }
`;

const UPDATE_ROLE = gql`
  mutation UpdateRole($id: UUID!, $role_name: String!) {
    updateRole(id: $id, role_name: $role_name) {
      id
      role_name
    }
  }
`;

const DELETE_ROLE = gql`
  mutation DeleteRole($id: UUID!) {
    deleteRole(id: $id)
  }
`;

const RolesPage = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedRole, setSelectedRole] = useState(null);
  const [isEditing, setIsEditing] = useState(false);

  // Queries
  const { data, loading, error } = useQuery(GET_ROLES);

  // Mutations
  const [createRole, { loading: createLoading }] = useMutation(CREATE_ROLE, {
    onCompleted: () => {
      toast.success('Rol creado exitosamente');
      setIsModalOpen(false);
    },
    onError: (error) => {
      toast.error(error.message);
    },
    refetchQueries: [{ query: GET_ROLES }]
  });

  const [updateRole, { loading: updateLoading }] = useMutation(UPDATE_ROLE, {
    onCompleted: () => {
      toast.success('Rol actualizado exitosamente');
      setIsModalOpen(false);
    },
    onError: (error) => {
      toast.error(error.message);
    },
    refetchQueries: [{ query: GET_ROLES }]
  });

  const [deleteRole, { loading: deleteLoading }] = useMutation(DELETE_ROLE, {
    onCompleted: () => {
      toast.success('Rol eliminado exitosamente');
      setIsDeleteModalOpen(false);
    },
    onError: (error) => {
      toast.error(error.message);
    },
    refetchQueries: [{ query: GET_ROLES }]
  });

  // Configuración de columnas para la tabla
  const columns = [
    {
      key: 'role_name',
      label: 'Nombre del Rol',
      sortable: true,
    },
  ];

  // Configuración de campos para el modal
  const formFields = [
    {
      name: 'role_name',
      label: 'Nombre del Rol',
      type: 'text',
      required: true,
      placeholder: 'Ingrese el nombre del rol',
      minLength: 2,
      maxLength: 50,
    },
  ];

  // Handlers
  const handleAdd = () => {
    setSelectedRole(null);
    setIsEditing(false);
    setIsModalOpen(true);
  };

  const handleEdit = (role) => {
    setSelectedRole(role);
    setIsEditing(true);
    setIsModalOpen(true);
  };

  const handleDelete = (role) => {
    setSelectedRole(role);
    setIsDeleteModalOpen(true);
  };

  const handleSave = async (formData) => {
    try {
      if (isEditing && selectedRole) {
        await updateRole({
          variables: {
            id: selectedRole.id,
            role_name: formData.role_name,
          },
        });
      } else {
        await createRole({
          variables: {
            role_name: formData.role_name,
          },
        });
      }
    } catch (error) {
      // Error handled by mutation onError
    }
  };

  const handleConfirmDelete = async () => {
    try {
      await deleteRole({
        variables: {
          id: selectedRole.id,
        },
      });
    } catch (error) {
      // Error handled by mutation onError
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedRole(null);
    setIsEditing(false);
  };

  const handleCloseDeleteModal = () => {
    setIsDeleteModalOpen(false);
    setSelectedRole(null);
  };

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">Error al cargar los roles: {error.message}</p>
        </div>
      </div>
    );
  }

  return (
    <DashboardLayout>
      <div className="p-6">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            Gestión de Roles
          </h1>
          <p className="text-gray-600">
            Administra los roles del sistema y sus permisos asociados
          </p>
        </div>

        <CRUDTable
          data={data?.roles || []}
          columns={columns}
          isLoading={loading}
          searchPlaceholder="Buscar roles..."
          searchFields={['role_name']}
          onAdd={handleAdd}
          onEdit={handleEdit}
          onDelete={handleDelete}
          addButtonText="Agregar Rol"
          emptyMessage="No hay roles disponibles"
        />

        <CRUDModal
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          onSave={handleSave}
          title={isEditing ? 'Editar Rol' : 'Agregar Rol'}
          initialData={selectedRole || {}}
          fields={formFields}
          isLoading={createLoading || updateLoading}
          isEditing={isEditing}
        />

        <DeleteConfirmModal
          isOpen={isDeleteModalOpen}
          onClose={handleCloseDeleteModal}
          onConfirm={handleConfirmDelete}
          title="Eliminar Rol"
          message="¿Estás seguro de que deseas eliminar este rol?"
          itemName={selectedRole?.role_name}
          isLoading={deleteLoading}
          isDangerous={true}
        />
      </div>
    </DashboardLayout>
  );
};

export default RolesPage;